<main role="main" aria-labelledby="page-title">
    <h1 id="page-title">Let's confirm the details</h1>
    <div class="info-content">
        <div class="info-block">
            <div class="data-heading">
                <div *ngIf="!isEditContactsMode">
                    <a
                        class="edit-switcher"
                        tabindex="0"
                        role="button"
                        gaEvent="edit_contacts_clicked"
                        gaCategory="edit_contacts"
                        gaLabel="Edit Contacts"
                        (keydown)="handleEditKeyDown($event)"
                        (click)="editContactsModeOn()"
                        >Edit</a
                    >
                </div>
                <h2>{{ firstName }} {{ lastName }}</h2>
            </div>
            <div class="data-holder">
                <ul class="data-list" *ngIf="!isEditContactsMode">
                    <li>
                        <picture>
                            <source
                                srcset="/assets/images/email-icon-white.svg"
                                media="(prefers-color-scheme: dark)"
                            />
                            <img src="/assets/images/email-icon.svg" alt="" role="presentation" width="24" height="24" />
                        </picture>
                        <span class="data-text">{{ email }}</span>
                    </li>
                    <li>
                        <picture>
                            <source
                                srcset="/assets/images/phone-black-icon-white.svg"
                                media="(prefers-color-scheme: dark)"
                            />
                            <img
                                src="/assets/images/phone-black-icon.svg"
                                alt=""
                                role="presentation"
                                width="24" height="24"
                            />
                        </picture>
                        <span class="data-text">{{ phone }}</span>
                    </li>
                </ul>
                <form
                    *ngIf="isEditContactsMode"
                    class="contact-form"
                    [formGroup]="contactForm"
                    (ngSubmit)="onContactFormSubmit()"
                >
                    <div class="input-holder">
                        <label
                            for="firstName"
                            *ngIf="contactForm.controls['firstName']"
                            [ngClass]="{ 'invalid-field': isFirstNameInvalid }"
                        >
                            <span class="label required">First Name</span>
                            <input
                                id="firstName"
                                type="text"
                                class="form-control st-input-field"
                                formControlName="firstName"
                                (input)="onNameInput($event)"
                                (focus)="onFieldFocus('firstName')"
                                (blur)="onFieldBlur()"
                                required
                                aria-required="true"
                                [attr.aria-invalid]="isFirstNameInvalid ? 'true' : 'false'"
                                [attr.aria-describedby]="
                                    isFirstNameInvalid ? 'firstName-error' : null
                                "
                                [ngClass]="{ 'warning-input': isFirstNameInvalid }"
                                maxlength="39"
                            />
                            <span *ngIf="isFirstNameInvalid" id="firstName-error" role="alert">
                                <span
                                    class="warning-text"
                                    *ngIf="contactForm.controls['firstName']?.hasError('maxlength')"
                                    >First name cannot exceed 39 characters.</span
                                >
                                <span
                                    class="warning-text"
                                    *ngIf="contactForm.get('firstName')?.hasError('pattern')"
                                    >First name must contain at least one letter.</span
                                >
                            </span>
                        </label>
                    </div>
                    <div class="input-holder">
                        <label
                            for="lastName"
                            *ngIf="contactForm.controls['lastName']"
                            [ngClass]="{ 'invalid-field': isLastNameInvalid }"
                        >
                            <span class="label required">Last Name</span>
                            <input
                                id="lastName"
                                type="text"
                                class="form-control st-input-field"
                                formControlName="lastName"
                                (input)="onNameInput($event)"
                                (focus)="onFieldFocus('lastName')"
                                (blur)="onFieldBlur()"
                                required
                                aria-required="true"
                                [attr.aria-invalid]="isLastNameInvalid ? 'true' : 'false'"
                                [attr.aria-describedby]="
                                    isLastNameInvalid ? 'lastName-error' : null
                                "
                                [ngClass]="{ 'warning-input': isLastNameInvalid }"
                                maxlength="39"
                            />
                            <span id="lastName-error" *ngIf="isLastNameInvalid" role="alert">
                                <span
                                    class="warning-text"
                                    *ngIf="contactForm.get('lastName')?.hasError('maxlength')"
                                    >Last name cannot exceed 39 characters.</span
                                >
                                <span
                                    class="warning-text"
                                    *ngIf="contactForm.get('lastName')?.hasError('pattern')"
                                    >Last name must contain at least one letter.</span
                                >
                            </span>
                        </label>
                    </div>
                    <div class="input-holder">
                        <label
                            for="email"
                            *ngIf="contactForm.controls['email']"
                            [ngClass]="{ 'invalid-field': isEmailInvalid }"
                        >
                            <span class="label required">Email</span>
                            <input
                                id="email"
                                type="email"
                                class="form-control st-input-field"
                                formControlName="email"
                                (input)="onEmailInput($event)"
                                (focus)="onFieldFocus('email')"
                                (blur)="onFieldBlur()"
                                required
                                aria-required="true"
                                [attr.aria-invalid]="isEmailInvalid ? 'true' : 'false'"
                                [attr.aria-describedby]="isEmailInvalid ? 'email-error' : null"
                                [ngClass]="{ 'warning-input': isEmailInvalid }"
                                maxlength="80"
                            />
                            <span id="email-error" *ngIf="isEmailInvalid" role="alert">
                                <span
                                    class="warning-text"
                                    *ngIf="contactForm.get('email')?.hasError('maxlength')"
                                    >Please ensure the email address does not exceed 80
                                    characters.</span
                                >
                                <span
                                    class="warning-text"
                                    *ngIf="
                                        contactForm.get('email')?.hasError('pattern') ||
                                        (contactForm.get('email')?.hasError('invalidDomain') &&
                                            contactForm.controls['email'].value)
                                    "
                                    >Please enter a valid email address.</span
                                >
                            </span>
                        </label>
                    </div>
                    <div class="input-holder">
                        <label
                            *ngIf="contactForm.controls['phone']"
                            [ngClass]="{ 'invalid-field': isPhoneInvalid }"
                            for="phone"
                        >
                            <span class="label required">Phone number</span>
                            <input
                                id="phone"
                                type="tel"
                                class="form-control st-input-field"
                                formControlName="phone"
                                (focus)="onFieldFocus('phone')"
                                (blur)="onFieldBlur()"
                                required
                                aria-required="true"
                                [attr.aria-invalid]="isPhoneInvalid ? 'true' : 'false'"
                                [attr.aria-describedby]="isPhoneInvalid ? 'phone-error' : null"
                                mask="(000) 000-0000"
                                [dropSpecialCharacters]="true"
                                [showMaskTyped]="true"
                                placeholder="(___) ___-____"
                                [ngClass]="{
                                    'warning-input': isPhoneInvalid
                                }"
                            />
                            <span id="phone-error" role="alert" *ngIf="isPhoneInvalid">
                                <span
                                    class="warning-text"
                                    *ngIf="contactForm.get('phone')?.hasError('minlength')"
                                    >Please enter a 10-digit phone number.</span
                                >
                                <span
                                    class="warning-text"
                                    *ngIf="
                                        contactForm
                                            .get('phone')
                                            ?.hasError('repeatingDigitsPhone') &&
                                        !contactForm.get('phone')?.hasError('minlength') &&
                                        !contactForm.get('phone')?.hasError('maxlength')
                                    "
                                    >Phone number can’t contain all the same digits</span
                                >
                            </span>
                        </label>
                    </div>
                    <div class="input-holder">
                        <button
                            type="button"
                            (click)="onContactFormSubmit()"
                            [disabled]="!contactForm.valid"
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
            <ul class="data-list">
                <li *ngIf="address">
                    <picture>
                        <source
                            srcset="/assets/images/geo-icon-white.svg"
                            media="(prefers-color-scheme: dark)"
                        />
                        <img src="/assets/images/geo-icon.svg" alt="" role="presentation" width="24" height="24" />
                    </picture>
                    <span class="data-text">{{ address }}</span>
                </li>
                <li *ngIf="levelsNumberText && squareFootage">
                    <picture>
                        <source
                            srcset="/assets/images/house-icon-white.svg"
                            media="(prefers-color-scheme: dark)"
                        />
                        <img src="/assets/images/house-icon.svg" alt="" role="presentation" width="24" height="24" />
                    </picture>
                    <span class="data-text"
                        >{{ levelsNumberText }}-level house, {{ squareFootage }} sq ft</span
                    >
                </li>
                <li>
                    <picture>
                        <source
                            srcset="/assets/images/pets-icon-white.svg"
                            media="(prefers-color-scheme: dark)"
                        />
                        <img src="/assets/images/pets-icon.svg" alt="" role="presentation" width="24" height="24" />
                    </picture>
                    <span class="data-text">
                        <ng-container *ngIf="petsNumber"
                            >I have {{ petsNumber }} pet<ng-container *ngIf="petsNumber > 1"
                                >s</ng-container
                            ></ng-container
                        >
                        <ng-container *ngIf="!petsNumber">I don't have pets</ng-container>
                    </span>
                </li>
            </ul>
        </div>
        <div class="info-block" *ngIf="selectedDate">
            <h3>Schedule</h3>
            <p>
                We will be in touch shortly to confirm details, including the date and time for your
                clean.
            </p>
            <div class="data-holder">
                <ul class="data-list">
                    <li>
                        <picture>
                            <source
                                srcset="/assets/images/calendar-icon-white.svg"
                                media="(prefers-color-scheme: dark)"
                            />
                            <img
                                src="/assets/images/calendar-icon.svg"
                                alt=""
                                role="presentation"
                                width="24" height="24"
                            />
                        </picture>
                        <span class="data-text">{{ frequencyTitle }}</span>
                    </li>
                </ul>
            </div>
            <ul class="data-list">
                <li>
                    <picture>
                        <source
                            srcset="/assets/images/datepicker-icon-white.svg"
                            media="(prefers-color-scheme: dark)"
                        />
                        <img src="/assets/images/datepicker-icon.svg" alt="" role="presentation" width="24" height="24" />
                    </picture>
                    <span class="data-text">
                        <ng-container *ngIf="!isOneTimeClean">Week 1: </ng-container>
                        {{ firstCleaningDateToString }}
                    </span>
                </li>
            </ul>
            <p class="info-text" *ngIf="!isOneTimeClean" role="status">
                <span class="info-mark"></span
                ><span>Future visits will be scheduled as requested and confirmed shortly.</span>
            </p>
        </div>
    </div>
    <div class="content-block">
        <h3 class="required">How can we contact you?</h3>
        <p>
            We'll be in touch shortly after you submit your request. If outside of business hours,
            expect follow up the next business day.
        </p>
        <app-multiple-picker
            [regularValues]="regularContactPrefValues"
            [specialValue]="specialContactPrefValue"
            [pickerData]="contactPrefData"
            [selectedValues]="contactPrefSelectedValues"
            (optionSelected)="onContactPrefSelected($event)"
        ></app-multiple-picker>
    </div>
    <div class="data-heading">
        <div>
            <a
                class="edit-switcher"
                tabindex="0"
                role="button"
                (keydown)="handleQuoteKeyDown($event)"
                gaEvent="edit_quotes_clicked"
                gaCategory="edit_quotes"
                gaLabel="Edit Quotes"
                (click)="navigateToQuotePage()"
                >Change quote</a
            >
        </div>
        <h3 *ngIf="frequencyTitle">{{ frequencyTitle }} cleaning</h3>
    </div>
    <p>Here is a summary of your request</p>
    <div class="info-table" *ngIf="selectedAppliancesObj?.length || selectedRoomsObj?.length">
        <table>
            <thead *ngIf="selectedRoomsObj?.length">
                <tr>
                    <th>Selected Rooms</th>
                    <th>Extra</th>
                </tr>
            </thead>
            <tbody *ngIf="selectedRoomsObj?.length">
                <tr *ngFor="let room of selectedRoomsObj">
                    <td class="name-cell">
                        <picture>
                            <source
                                [srcset]="room.imageSrcDarkTheme"
                                media="(prefers-color-scheme: dark)"
                            />
                            <img [src]="room.imageSrc" [alt]="'Image of ' + room.roomName" width="24" height="24" />
                        </picture>
                        {{ room.roomName }}
                    </td>
                    <td class="extra-cell">
                        <ng-container *ngIf="room.isDetailed">
                            <input type="checkbox" [checked]="true" [disabled]="true" />
                            <label>Extra Sparkle</label>
                        </ng-container>
                    </td>
                </tr>
            </tbody>
            <thead *ngIf="selectedAppliancesObj?.length">
                <tr>
                    <th>Additional Services</th>
                    <th></th>
                </tr>
            </thead>
            <tbody *ngIf="selectedAppliancesObj?.length">
                <tr *ngFor="let appliance of selectedAppliancesObj">
                    <td class="name-cell">
                        <picture>
                            <source
                                [srcset]="appliance.imageSrcDarkTheme"
                                media="(prefers-color-scheme: dark)"
                            />
                            <img [src]="appliance.imageSrc" [alt]="'Image of ' + appliance.name" width="24" height="24" />
                        </picture>
                        {{ appliance.name }}
                    </td>
                    <td class="extra-cell">
                        <input type="checkbox" [checked]="true" [disabled]="true" />
                        <label>One-time</label>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
    <div class="info-block price-block">
        <div class="price-holder">
            <h4>Approximate total</h4>
            <div class="price">
                ${{ firstTimePrice }}
                <ng-container *ngIf="!isOneTimeClean">/ ${{ recurringPrice }}</ng-container>
            </div>
            <span class="price-text">
                <ng-container *ngIf="!isOneTimeClean">First clean price /</ng-container>
                {{ frequencyTitle }} price
            </span>
        </div>
        <ul class="price-info">
            <li *ngIf="!isDetailedMode">First-clean price includes a promotional offer and may not be combined with any other discounts.</li>
            <li>
                We will contact you to confirm your service details and finalize your customized cleaning price{{ websiteOfferName ? ', including the selected offer (' + websiteOfferName + ')' : '' }}.
            </li>
            <li>This quote is not binding upon you or Merry Maids.</li>
            <li>
                These are the approximate charges to be assessed for the services identified in this
                quote.
            </li>
            <li>Prices exclude taxes.</li>
            <li>Your local Merry Maids location will confirm the charges.</li>
        </ul>
    </div>
    <div class="content-block">
        <form [formGroup]="notesForm">
            <h3 id="specific-needs-label">Specific needs</h3>
            <p>
                Do you have an antique table requiring special attention?<br />
                Concern about rescheduling options?<br />
                Let us know here so we can discuss when we connect!
            </p>
            <textarea
                *ngIf="notesForm.controls['notes']"
                formControlName="notes"
                type="text"
                class="notes-textarea"
                placeholder="What else is important to you?"
                aria-labelledby="specific-needs-label"
                [attr.aria-invalid]="isNotesInvalid"
                [attr.aria-describedby]="isNotesInvalid ? 'notes-error' : null"
                [ngClass]="{ 'warning-input': isNotesInvalid }"
                maxlength="750"
            ></textarea>
            <span class="warning-text" role="alert" id="notes-error" *ngIf="isNotesInvalid">
                <ng-container *ngIf="notesForm.get('notes')?.hasError('maxlength')"
                    >Max length should be no more than 750 characters</ng-container
                >
                <ng-container *ngIf="notesForm.get('notes')?.hasError('pattern')"
                    >Looks like you're using characters that aren't allowed.</ng-container
                >
            </span>
        </form>
    </div>
</main>
